import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Card from '@components/ui/Card';
import Badge from '@components/ui/Badge';
import Button from '@components/ui/Button';
import Input from '@components/ui/Input';
import LoadingSpinner from '@components/ui/LoadingSpinner';
import ErrorState from '@components/ui/ErrorState';
import Modal from '@components/ui/Modal';
import SigningRequests, { SigningRequestSummary } from '@components/ui/SigningRequests';
import './Companies.css';
import './PeopleDetail.css';

// ─── Types ────────────────────────────────────────────────────────────────────

interface LinkedPerson {
  id: string;
  full_name: string;
  email: string | null;
  status: string;
  role_or_title: string | null;
}

interface LinkedProject {
  id: string;
  project_name: string;
  status: string;
  created_at: string;
}

interface CompanyDocument {
  id: string;
  document_name: string;
  document_type: string;
  uploaded_at: string;
  created_at: string;
}

interface CompanyDetail {
  id: string;
  company_name: string;
  website: string | null;
  sector: string | null;
  general_notes: string | null;
  created_at: string;
  updated_at: string;
  linked_people: LinkedPerson[];
  linked_projects: LinkedProject[];
  documents?: CompanyDocument[];
  signing_requests?: SigningRequestSummary[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function personStatusVariant(status: string): 'default' | 'success' | 'warning' | 'danger' {
  switch (status) {
    case 'Customer':
      return 'success';
    case 'Opportunity':
    case 'Qualified':
      return 'warning';
    case 'Unqualified':
      return 'danger';
    default:
      return 'default';
  }
}

function projectStatusVariant(status: string): 'default' | 'success' | 'warning' | 'danger' {
  switch (status) {
    case 'Completed':
      return 'success';
    case 'Work Started':
    case 'Proposal Accepted':
      return 'warning';
    default:
      return 'default';
  }
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

// ─── Edit form state ──────────────────────────────────────────────────────────

interface EditFormState {
  name: string;
  website: string;
  sector: string;
  notes: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

const CompanyDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [company, setCompany] = useState<CompanyDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleteSaving, setDeleteSaving] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [form, setForm] = useState<EditFormState>({
    name: '',
    website: '',
    sector: '',
    notes: '',
  });

  // Link person modal state
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<LinkedPerson[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [linkingPersonId, setLinkingPersonId] = useState<string | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);

  // Document upload state
  const [uploadDocOpen, setUploadDocOpen] = useState(false);
  const [uploadDocType, setUploadDocType] = useState('Other');
  const [uploadDocFile, setUploadDocFile] = useState<File | null>(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [uploadDocError, setUploadDocError] = useState<string | null>(null);

  const fetchCompany = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/companies/${id}`);
      if (res.status === 404) {
        setError('Company not found.');
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json() as { success: boolean; data: CompanyDetail };
      if (!json.success) throw new Error('API returned failure');
      setCompany(json.data);
    } catch (err) {
      console.error('Failed to fetch company:', err);
      setError('Failed to load company. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchCompany();
  }, [fetchCompany]);

  const handleUploadDocument = async () => {
    if (!company || !uploadDocFile) return;
    setUploadingDoc(true);
    setUploadDocError(null);
    try {
      const formData = new FormData();
      formData.append('file', uploadDocFile);
      formData.append('document_name', uploadDocFile.name);
      formData.append('document_type', uploadDocType);
      formData.append('company_id', company.id);

      const res = await fetch('/api/documents', { method: 'POST', body: formData });
      const json: any = await res.json().catch(() => ({}));
      if (!res.ok || !json?.success) {
        throw new Error(json?.message || `HTTP ${res.status}`);
      }
      setUploadDocOpen(false);
      setUploadDocFile(null);
      setUploadDocType('Other');
      await fetchCompany();
    } catch (err) {
      setUploadDocError(err instanceof Error ? err.message : 'Failed to upload document.');
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleDeleteDocument = async (docId: string, docName: string) => {
    if (!confirm(`Are you sure you want to delete this document?\n\n${docName}\n\nThis action cannot be undone.`)) return;
    try {
      const res = await fetch(`/api/documents/${docId}`, { method: 'DELETE' });
      const json: any = await res.json().catch(() => ({}));
      if (!res.ok || !json?.success) {
        throw new Error(json?.message || `HTTP ${res.status}`);
      }
      await fetchCompany();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete document.');
    }
  };

  // Populate form when entering edit mode
  const startEditing = () => {
    if (!company) return;
    setForm({
      name: company.company_name ?? '',
      website: company.website ?? '',
      sector: company.sector ?? '',
      notes: company.general_notes ?? '',
    });
    setSaveError(null);
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
    setSaveError(null);
  };

  const handleSave = async () => {
    if (!id || !company) return;
    if (!form.name.trim()) {
      setSaveError('Company name is required.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const res = await fetch(`/api/companies/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          website: form.website.trim() || null,
          sector: form.sector.trim() || null,
          notes: form.notes.trim() || null,
        }),
      });
      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        throw new Error((json as { message?: string }).message ?? `HTTP ${res.status}`);
      }
      const json = await res.json() as { success: boolean; data: CompanyDetail };
      if (!json.success) throw new Error('Save failed');
      // Merge updated fields, preserve linked data
      setCompany({
        ...json.data,
        linked_people: company.linked_people,
        linked_projects: company.linked_projects,
      });
      setEditing(false);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save changes.';
      setSaveError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCompany = async () => {
    if (!id || !company) return;

    const ok = window.confirm(
      `Are you sure you want to delete this company?\n\n${company.company_name}\n\nThis action cannot be undone.`
    );
    if (!ok) return;

    setDeleteSaving(true);
    setDeleteError(null);
    try {
      const res = await fetch(`/api/companies/${id}`, { method: 'DELETE' });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !(json as any)?.success) {
        throw new Error((json as any)?.message ?? `HTTP ${res.status}`);
      }
      navigate('/companies');
    } catch (err: unknown) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete company.');
    } finally {
      setDeleteSaving(false);
    }
  };

  // ─── Link person handlers ─────────────────────────────────────────────────────

  const openLinkModal = () => {
    setLinkModalOpen(true);
    setSearchQuery('');
    setSearchResults([]);
    setLinkError(null);
  };

  const closeLinkModal = () => {
    setLinkModalOpen(false);
    setSearchQuery('');
    setSearchResults([]);
    setLinkError(null);
  };

  const searchPeople = useCallback(async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }
    setSearchLoading(true);
    try {
      const res = await fetch(`/api/people?q=${encodeURIComponent(query)}&page_size=20`);
      if (!res.ok) throw new Error('Search failed');
      const json = await res.json() as { success: boolean; data: LinkedPerson[] };
      if (!json.success) throw new Error('Search failed');
      // Filter out people already linked to this company
      const filtered = json.data.filter(
        (p) => !company?.linked_people.some((lp) => lp.id === p.id)
      );
      setSearchResults(filtered);
    } catch (err) {
      console.error('Search error:', err);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, [company]);

  useEffect(() => {
    if (!linkModalOpen) return;
    const timer = setTimeout(() => {
      searchPeople(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, linkModalOpen, searchPeople]);

  const linkPersonToCompany = async (personId: string) => {
    if (!id) return;
    setLinkingPersonId(personId);
    setLinkError(null);
    try {
      const res = await fetch(`/api/people/${personId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ company_id: id }),
      });
      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        throw new Error((json as { message?: string }).message ?? 'Failed to link person');
      }
      const json = await res.json() as { success: boolean };
      if (!json.success) throw new Error('Failed to link person');
      
      // Refresh company data to show newly linked person
      await fetchCompany();
      closeLinkModal();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to link person.';
      setLinkError(message);
    } finally {
      setLinkingPersonId(null);
    }
  };

  // ─── Loading / error states ──────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="detail-loading">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !company) {
    return (
      <Card>
        <ErrorState
          message={error ?? 'Company not found.'}
          onRetry={error !== 'Company not found.' ? fetchCompany : undefined}
        />
      </Card>
    );
  }

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="company-detail-page">
      {/* Back navigation */}
      <button className="back-link" onClick={() => navigate('/companies')} type="button">
        ← Companies
      </button>

      {/* Page header */}
      <div className="detail-header">
        <div className="detail-header-left">
          <h1 className="detail-name">{company.company_name}</h1>
          {company.sector && (
            <p className="detail-role">{company.sector}</p>
          )}
        </div>
        <div className="detail-header-right">
          {!editing && (
            <>
              <Button variant="secondary" size="sm" onClick={startEditing}>
                Edit
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={handleDeleteCompany}
                disabled={deleteSaving}
              >
                {deleteSaving ? 'Deleting…' : 'Delete'}
              </Button>
            </>
          )}
        </div>
      </div>

      {deleteError && (
        <Card>
          <p className="edit-save-error">{deleteError}</p>
        </Card>
      )}

      {/* ── View mode ─────────────────────────────────────────────────────────── */}
      {!editing ? (
        <>
          {/* Company details */}
          <Card>
            <h2 className="section-title">Company details</h2>
            <div className="detail-fields">
              <div className="detail-field">
                <span className="detail-field-label">Website</span>
                {company.website ? (
                  <a
                    href={company.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="detail-field-link"
                  >
                    {company.website}
                  </a>
                ) : (
                  <span className="detail-field-empty">—</span>
                )}
              </div>
              {company.sector && (
                <div className="detail-field">
                  <span className="detail-field-label">Sector</span>
                  <span className="detail-field-value">{company.sector}</span>
                </div>
              )}
            </div>
          </Card>

          {/* General notes */}
          <Card>
            <h2 className="section-title">Notes</h2>
            {company.general_notes ? (
              <p className="detail-notes">{company.general_notes}</p>
            ) : (
              <p className="detail-field-empty">No notes yet.</p>
            )}
          </Card>

          {/* Linked people */}
          <Card>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--spacing-md)' }}>
              <h2 className="section-title" style={{ margin: 0 }}>
                People
                {company.linked_people.length > 0 && (
                  <span style={{ fontWeight: 400, marginLeft: 'var(--spacing-xs)' }}>
                    ({company.linked_people.length})
                  </span>
                )}
              </h2>
              <Button variant="secondary" size="sm" onClick={openLinkModal}>
                + Link person
              </Button>
            </div>
            {company.linked_people.length === 0 ? (
              <p className="detail-field-empty">No people linked to this company.</p>
            ) : (
              <div className="detail-people-list">
                {company.linked_people.map((person) => (
                  <Link
                    key={person.id}
                    to={`/people/${person.id}`}
                    className="detail-person-row"
                  >
                    <div className="detail-person-info">
                      <span className="detail-person-name">{person.full_name}</span>
                      {person.role_or_title && (
                        <span className="detail-person-role">{person.role_or_title}</span>
                      )}
                    </div>
                    <Badge variant={personStatusVariant(person.status)}>
                      {person.status}
                    </Badge>
                  </Link>
                ))}
              </div>
            )}
          </Card>

          {/* Linked projects */}
          <Card>
            <h2 className="section-title">
              Projects
              {company.linked_projects.length > 0 && (
                <span style={{ fontWeight: 400, marginLeft: 'var(--spacing-xs)' }}>
                  ({company.linked_projects.length})
                </span>
              )}
            </h2>
            {company.linked_projects.length === 0 ? (
              <p className="detail-field-empty">No projects linked to this company.</p>
            ) : (
              <div className="detail-projects-list">
                {company.linked_projects.map((project) => (
                  <Link
                    key={project.id}
                    to={`/projects/${project.id}`}
                    className="detail-project-row"
                  >
                    <span className="detail-project-name">{project.project_name}</span>
                    <Badge variant={projectStatusVariant(project.status)}>
                      {project.status}
                    </Badge>
                  </Link>
                ))}
              </div>
            )}
          </Card>

          {/* Documents */}
          <Card>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
              <h2 className="section-title" style={{ margin: 0 }}>Documents</h2>
              {!uploadDocOpen && (
                <Button variant="secondary" size="sm" onClick={() => setUploadDocOpen(true)}>
                  Upload document
                </Button>
              )}
            </div>

            {(company.documents ?? []).length === 0 ? (
              <p className="detail-field-empty">No documents uploaded.</p>
            ) : (
              <div className="detail-projects-list">
                {(company.documents ?? []).map((d) => (
                  <div
                    key={d.id}
                    className="detail-project-row"
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                  >
                    <a
                      href={`/api/documents/${d.id}/download`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        cursor: 'pointer',
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--spacing-sm)',
                      }}
                    >
                      <span className="detail-project-name">{d.document_name}</span>
                      <Badge variant="default">{d.document_type}</Badge>
                    </a>
                    <Button variant="danger" size="sm" onClick={() => handleDeleteDocument(d.id, d.document_name)}>
                      Delete
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {uploadDocOpen && (
              <div className="edit-form" style={{ marginTop: '1rem', borderTop: '1px solid #e2e8f0', paddingTop: '1rem' }}>
                <div className="input-wrapper">
                  <label className="input-label">Document type</label>
                  <select
                    className="input"
                    value={uploadDocType}
                    onChange={(e) => setUploadDocType(e.target.value)}
                    disabled={uploadingDoc}
                  >
                    <option value="Meeting notes">Meeting notes</option>
                    <option value="Signed document">Signed document</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="input-wrapper">
                  <label className="input-label">File</label>
                  <input
                    type="file"
                    className="input"
                    onChange={(e) => {
                      setUploadDocFile(e.target.files?.[0] || null);
                      setUploadDocError(null);
                    }}
                    disabled={uploadingDoc}
                  />
                </div>
                {uploadDocError && <p className="edit-save-error">{uploadDocError}</p>}
                <div className="edit-actions">
                  <Button variant="primary" onClick={handleUploadDocument} disabled={uploadingDoc || !uploadDocFile}>
                    {uploadingDoc ? 'Uploading…' : 'Upload'}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setUploadDocOpen(false);
                      setUploadDocFile(null);
                      setUploadDocError(null);
                    }}
                    disabled={uploadingDoc}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </Card>

          {/* Signing requests */}
          <SigningRequests
            companyId={company.id}
            requests={company.signing_requests ?? []}
            onChanged={fetchCompany}
          />

          {/* Metadata */}
          <div className="detail-meta">
            <span>Created {formatDate(company.created_at)}</span>
            <span>Updated {formatDate(company.updated_at)}</span>
          </div>
        </>
      ) : (
        /* ── Edit mode ──────────────────────────────────────────────────────── */
        <Card>
          <h2 className="section-title">Edit company</h2>
          <div className="edit-form">
            <Input
              label="Company name *"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Company name"
            />
            <Input
              label="Website"
              type="url"
              value={form.website}
              onChange={(e) => setForm({ ...form, website: e.target.value })}
              placeholder="https://example.com"
            />
            <Input
              label="Sector"
              value={form.sector}
              onChange={(e) => setForm({ ...form, sector: e.target.value })}
              placeholder="e.g. Technology, Healthcare, Education"
            />
            <div className="input-wrapper">
              <label className="input-label">Notes</label>
              <textarea
                className="input edit-notes-textarea"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="General notes about this company…"
                rows={5}
              />
            </div>

            {saveError && (
              <p className="edit-save-error">{saveError}</p>
            )}

            <div className="edit-actions">
              <Button
                variant="primary"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Save changes'}
              </Button>
              <Button
                variant="secondary"
                onClick={cancelEditing}
                disabled={saving}
              >
                Cancel
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* ── Link person modal ────────────────────────────────────────────────── */}
      <Modal isOpen={linkModalOpen} onClose={closeLinkModal} title="Link person to company" size="md">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
          <Input
            label="Search for a person"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Type name or email..."
            autoFocus
          />

          {linkError && (
            <p style={{ color: 'var(--color-danger)', fontSize: 'var(--font-size-sm)' }}>
              {linkError}
            </p>
          )}

          {searchLoading && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--spacing-lg)' }}>
              <LoadingSpinner size="sm" />
            </div>
          )}

          {!searchLoading && searchQuery.trim() && searchResults.length === 0 && (
            <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)', textAlign: 'center', padding: 'var(--spacing-lg)' }}>
              No people found matching "{searchQuery}"
            </p>
          )}

          {!searchLoading && searchResults.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-xs)', maxHeight: '400px', overflowY: 'auto' }}>
              {searchResults.map((person) => (
                <button
                  key={person.id}
                  type="button"
                  onClick={() => linkPersonToCompany(person.id)}
                  disabled={linkingPersonId !== null}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: 'var(--spacing-md)',
                    border: '1px solid var(--color-border)',
                    borderRadius: 'var(--radius-md)',
                    background: linkingPersonId === person.id ? 'var(--color-bg-secondary)' : 'var(--color-bg-primary)',
                    cursor: linkingPersonId !== null ? 'not-allowed' : 'pointer',
                    textAlign: 'left',
                    transition: 'background-color 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    if (linkingPersonId === null) {
                      e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (linkingPersonId !== person.id) {
                      e.currentTarget.style.backgroundColor = 'var(--color-bg-primary)';
                    }
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-xs)' }}>
                    <span style={{ fontWeight: 500, color: 'var(--color-text-primary)' }}>
                      {person.full_name}
                    </span>
                    {person.email && (
                      <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                        {person.email}
                      </span>
                    )}
                  </div>
                  {linkingPersonId === person.id ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    <Badge variant={personStatusVariant(person.status)}>{person.status}</Badge>
                  )}
                </button>
              ))}
            </div>
          )}

          {!searchQuery.trim() && (
            <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)', textAlign: 'center', padding: 'var(--spacing-lg)' }}>
              Start typing to search for people
            </p>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default CompanyDetail;
